import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import Modal from '@saucelabs/ds-modal';
import { modalConnectV2 } from '@saucelabs/sl-container-modal';
import { Loader } from '@saucelabs/sl-component-loader';
import { IconChecklistFill, IconBrowserUnknownFill } from '@saucelabs/ds-icons';
import { Typography } from '@saucelabs/ds-typography';

import { actions as realDeviceSelectionActions } from '../../realDeviceSelection';
import constants from '../constants';
import operations from '../operations';
import selectors from '../selectors';
import { useGetModalState, STATES } from '../hooks/useGetModalState';
import PresetItem from './PresetItem';
import Styles from './RecentsModal.styles';

const UPDATE_BANNER_TRANSITION_DURATION_MS = 300;

const RecentsModalComponent: React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const currentState = useGetModalState();
  const { elements } = useSelector(selectors.presetsSelector);

  useEffect(() => {
    void dispatch(operations.getPresetsWithMetadata());
  }, [dispatch]);

  useEffect(() => {
    let wasPollingInProgress: boolean;
    const hasRdcPreset = elements.some((preset) => preset.isRdcDevice());

    if (currentState === STATES.SUCCESS && hasRdcPreset) {
      wasPollingInProgress = dispatch(realDeviceSelectionActions.pollDeviceState());
    }

    return () => {
      // Stop polling if it was initiated from here
      if (!wasPollingInProgress) {
        dispatch(realDeviceSelectionActions.stopFetchingMobileRealDeviceData());
      }
    };
  }, [dispatch, elements, currentState]);

  return (
    <Modal
      emphasis="low"
      title="Recents"
    >
      {(currentState === STATES.SUCCESS || currentState === STATES.UPDATING) && (
        <Typography
          className="mb-2"
          variant="body-large"
        >
          {`Up to ${constants.MAX_RECENTS_ITEMS} recent items will be displayed here.`}
        </Typography>
      )}

      <CSSTransition
        classNames={{
          enter: Styles['banner-enter'],
          enterActive: Styles['banner-enter-active'],
          enterDone: Styles['banner-enter-done'],
          exit: Styles['banner-exit'],
          exitActive: Styles['banner-exit-active'],
        }}
        data-qa="recents-modal-update-banner"
        in={currentState === STATES.UPDATING}
        mountOnEnter
        timeout={UPDATE_BANNER_TRANSITION_DURATION_MS}
        unmountOnExit
      >
        <div
          className={`
            flex justify-center items-center
            overflow-hidden bg-gray-50
            transition-height duration-${UPDATE_BANNER_TRANSITION_DURATION_MS}
          `}
        >
          <Loader
            className="mr-1"
            fontSize={1}
          />
          <Typography
            className="my-1 text-medium-emphasis font-semibold"
            variant="body-small"
          >
            Checking for updates
          </Typography>
        </div>
      </CSSTransition>

      <Loader
        className="mx-auto my-7"
        show={currentState === STATES.LOADING}
      >
        {currentState === STATES.EMPTY && (
          <div className="text-center pb-5">
            <IconChecklistFill className="text-gray-400 fill-current" />

            <Typography variant="heading-4">No recent configurations to show</Typography>

            <Typography
              className="text-medium-emphasis"
              variant="body-large"
            >
              Your recently used configurations will be listed here once you start testing.
            </Typography>
          </div>
        )}

        {currentState === STATES.ERROR && (
          <div className="text-center pb-5">
            <IconBrowserUnknownFill className="text-orange-600 fill-current" />

            <Typography variant="heading-4">Unable to load recent configurations</Typography>

            <Typography
              className="text-medium-emphasis"
              variant="body-large"
            >
              Please refresh the page and try again.
            </Typography>
          </div>
        )}

        {(currentState === STATES.SUCCESS || currentState === STATES.UPDATING) &&
          elements.map((preset, index) => (
            <PresetItem
              key={preset.id}
              index={index}
              preset={preset}
            />
          ))}
      </Loader>
    </Modal>
  );
};

const RecentsModal = modalConnectV2(
  constants.RECENTS_MODAL_IDENTIFIER,
  RecentsModalComponent,
);

export default RecentsModal;
export { RecentsModalComponent };
